"use client";

import { useRef, useState, useEffect, useCallback } from "react";
import { cn } from "@/lib/tailwind";
import AngleLeftIcon from "@/assets/icons/arrow-left.svg?react";
import AngleRightIcon from "@/assets/icons/arrow-right.svg?react";

type ResponsiveGap = number | { mobile: number; desktop: number };

interface ResponsiveCarouselProps {
  children: React.ReactNode[];
  gap?: ResponsiveGap;
  className?: string;
  showDots?: boolean;
  arrowPosition?: "inline" | "outside";

}

const ResponsiveCarousel = ({
  children,
  gap = 0,
  className,
  showDots = false,
  arrowPosition = "inline",
}: ResponsiveCarouselProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [showArrows, setShowArrows] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [isDesktop, setIsDesktop] = useState<boolean>(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const mobileGap = typeof gap === "object" ? gap.mobile : (gap ?? 0);
  const desktopGap = typeof gap === "object" ? gap.desktop : (gap ?? 0);

  const checkScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el || el.clientWidth === 0) return;
    const hasOverflow = el.scrollWidth > el.clientWidth + 4;
    setShowArrows(hasOverflow && isDesktop);
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    if (showDots) {
      const firstChild = el.firstElementChild as HTMLElement | null;
      const computedGap = parseFloat(getComputedStyle(el).gap) || 0;
      const itemWidth = firstChild ? firstChild.offsetWidth + computedGap : 1;
      setCurrentIndex(Math.round(el.scrollLeft / itemWidth));
    }
  }, [isDesktop, showDots]);

  useEffect(() => {

    const raf = requestAnimationFrame(checkScrollState);

    const handleResize = () => requestAnimationFrame(checkScrollState);

    window.addEventListener("resize", handleResize);

    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", handleResize);
      resizeObserver.disconnect();
    };
  }, [checkScrollState]);

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const firstChild = el.firstElementChild as HTMLElement | null;
    const computedGap = parseFloat(getComputedStyle(el).gap) || 0;
    const scrollAmount = firstChild ? firstChild.offsetWidth + computedGap : el.clientWidth * 0.75;
    el.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  return (
    <div className="relative mx-auto w-full">
      <div ref={containerRef} className="flex w-full items-center gap-6">
        {showArrows && (
          <button
            onClick={() => scroll("left")}
            disabled={!canScrollLeft}
            className={cn(
              arrowPosition === "outside"
                ? "absolute -left-8 md:-left-12 top-1/2 -translate-y-1/2 z-10"
                : "flex-shrink-0",
              "py-2 pr-2 transition-opacity flex items-center justify-center",
              canScrollLeft
                ? "cursor-pointer opacity-100"
                : "cursor-default opacity-30",
            )}
            aria-label="Scroll left"
          >
            <AngleLeftIcon className="h-4 w-4" />
          </button>
        )}

        <div
          ref={scrollRef}
          onScroll={checkScrollState}
          className={cn(
            "no-scrollbar flex min-w-0 flex-1 touch-pan-x overflow-x-auto [gap:var(--carousel-gap-mobile)] md:[gap:var(--carousel-gap-desktop)] justify-center",
            className,
          )}
          style={{
            "--carousel-gap-mobile": `${mobileGap}px`,
            "--carousel-gap-desktop": `${desktopGap}px`,
          } as React.CSSProperties}
        >
          {children.map((child, i) => (
            <div key={i} className="flex flex-shrink-0 items-center justify-center">
              {child}
            </div>
          ))}
        </div>

        {showArrows && (
          <button
            onClick={() => scroll("right")}
            disabled={!canScrollRight}
            className={cn(
              arrowPosition === "outside"
                ? "absolute -right-8 md:-right-12 top-1/2 -translate-y-1/2 z-10"
                : "flex-shrink-0",
              "py-2 pl-2 transition-opacity flex items-center justify-center",
              canScrollRight
                ? "cursor-pointer opacity-100"
                : "cursor-default opacity-30",
            )}
            aria-label="Scroll right"
          >
            <AngleRightIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {showDots && (
        <div className="flex md:hidden justify-center gap-2 mt-6">
          {children.map((_, i) => (
            <span
              key={i}
              className={cn(
                "w-2 h-2 rounded-full transition-colors",
                currentIndex === i ? "bg-black" : "bg-gray-300",
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ResponsiveCarousel;
